import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const COOKIE_CONSENT_KEY = 'neeli_cookie_consent';
const MIGRATION_FLAG = 'neeli_cookie_consent_migrated';

interface StoredPreferences {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  consentedAt: string;
}

/**
 * Migrates localStorage cookie consent to database when user signs in.
 * Should be called within AuthProvider to listen for SIGNED_IN events.
 */
export function useCookieConsentMigration() {
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Defer to avoid Supabase deadlock
          setTimeout(() => {
            migrateConsent(session.user.id);
          }, 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const migrateConsent = async (userId: string) => {
    try {
      // Check if already migrated
      const migrated = localStorage.getItem(MIGRATION_FLAG);
      if (migrated === userId) return;

      // Check for stored consent
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) return;

      const preferences: StoredPreferences = JSON.parse(stored);
      
      // Record analytics consent directly
      if (preferences.analytics !== undefined) {
        await supabase.from('gdpr_consent').insert({
          user_id: userId,
          consent_type: 'analytics_cookies',
          consented: preferences.analytics,
          consent_text: 'User consented to analytics cookies via cookie banner',
          user_agent: navigator.userAgent,
        });
      }

      // Record marketing consent directly
      if (preferences.marketing !== undefined) {
        await supabase.from('gdpr_consent').insert({
          user_id: userId,
          consent_type: 'marketing_cookies',
          consented: preferences.marketing,
          consent_text: 'User consented to marketing cookies via cookie banner',
          user_agent: navigator.userAgent,
        });
      }

      // Mark as migrated for this user
      localStorage.setItem(MIGRATION_FLAG, userId);
      
      console.log('[CookieConsent] Successfully migrated preferences to database');
    } catch (error) {
      console.error('[CookieConsent] Failed to migrate preferences:', error);
    }
  };
}
